import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { db } from '@/lib/db';
import type { CourseHole, GroupRoundEnriched, Member, ScoreChangeLog } from '@/lib/db/types';
import { publishGroupRound } from '@/lib/games/publish-group-round';
import { removeRoundFromPlayTabCache } from '@/lib/play-tab-data';
import { isRoundParticipant, roundHasMoneyStakes } from '@/lib/games/round-recap';
import { gameResultsFromSettlement } from '@/lib/games/game-results';
import type { GameResult } from '@/lib/games/engine/types';
import type { PressSettlementLine } from '@/lib/games/engine/presses';
import { appliesMoney, stakeFromDbRow, scoringTypeLabel, migrateScoringType } from '@/lib/games/engine/stakes-helpers';
import {
  buildGridScoresByPlayer,
  buildPayoutRows,
  hasHoleByHoleScores,
  payoutSentence,
  scorecardRangeForHoles,
  totalsOnlyRows,
} from '@/lib/games/round-results-display';
import { processPointsAfterRound, summarizeClubPointsForMember } from '@/lib/points/processor';
import { GroupScorecardGrid } from '@/components/play/GroupScorecardGrid';
import { uploadFile, deleteUploadedFile } from '@/lib/storage';
import { Sheet } from '@/components/shared/Sheet';
import { AlertDialog } from '@/components/shared/AlertDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

interface Props {
  member: Member;
  roundId: string;
  onClose: () => void;
  onPosted?: () => void;
  /** When true (e.g. feed "View money results"), open settlement details by default. */
  focusSettlement?: boolean;
}

function formatMoney(cents: number): string {
  const dollars = cents / 100;
  const sign = dollars >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(dollars).toFixed(0)}`;
}

export function RoundResultsSheet({
  member,
  roundId,
  onClose,
  onPosted,
  focusSettlement = false,
}: Props) {
  const [round, setRound] = useState<GroupRoundEnriched | null>(null);
  const [courseName, setCourseName] = useState('');
  const [holes, setHoles] = useState<CourseHole[]>([]);
  const [log, setLog] = useState<ScoreChangeLog[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [showScorecard, setShowScorecard] = useState(true);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [showSettlement, setShowSettlement] = useState(focusSettlement);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [postBody, setPostBody] = useState('');
  const [postError, setPostError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShowSettlement(focusSettlement);
  }, [focusSettlement, roundId]);

  useEffect(() => {
    void (async () => {
      const data = await db.groupRounds.get(roundId);
      setRound(data);
      if (data?.course_id) {
        const courses = await db.courses.list();
        setCourseName(courses.find((c) => c.id === data.course_id)?.name || '');
        const holeRows = await db.courses.holes(data.course_id);
        setHoles([...holeRows].sort((a, b) => a.hole_num - b.hole_num));
      }
      const entries = await db.groupRounds.getScoreLog(roundId);
      setLog(entries);
    })();
  }, [roundId]);

  const settlement = round?.settlement;
  const gameResults = gameResultsFromSettlement(settlement?.game_results);
  const isCasual = round?.config?.game_mode === 'casual' || gameResults.length <= 1;
  const hasMoneyStakes = round ? roundHasMoneyStakes(round) : false;
  const canPostRecap = round ? isRoundParticipant(round, member.id) : false;
  const isOrganizer = round?.organizer_id === member.id;
  const myPlayerKey = round?.players?.find((p) => p.member_id === member.id)?.player_key;

  const clubPoints = useMemo(() => {
    if (!round || !holes.length) return null;
    const awards = processPointsAfterRound({ round, gameResults, holes });
    return summarizeClubPointsForMember(awards, member.id);
  }, [round, gameResults, holes, member.id]);

  const hasHoleScores = useMemo(
    () => hasHoleByHoleScores(round?.scores),
    [round?.scores],
  );

  const gridPlayers = useMemo(() => {
    if (!round?.players) return [];
    return buildGridScoresByPlayer(round.players, round.scores || [], member.id);
  }, [round?.players, round?.scores, member.id]);

  const scorecardRange = useMemo(
    () => scorecardRangeForHoles(round?.holes, holes),
    [round?.holes, holes],
  );

  const totalsFallback = useMemo(() => {
    if (!round?.players) return [];
    return totalsOnlyRows(round.players, round.scores || [], member.id);
  }, [round?.players, round?.scores, member.id]);

  const payoutRows = useMemo(
    () => buildPayoutRows(settlement?.simplified_payments, round?.players || [], member.id),
    [settlement?.simplified_payments, round?.players, member.id],
  );

  const playerName = (key: string) => round?.players?.find((p) => p.player_key === key)?.display_name || key;

  const gameMoneyLine = (g: GameResult): string | null => {
    if (!hasMoneyStakes || !myPlayerKey) return null;
    const stake = round?.games?.find((row) => row.format_id === g.formatId);
    const config = stake ? stakeFromDbRow(g.formatId, (stake.stakes || {}) as Record<string, unknown>) : null;
    if (config && !appliesMoney(config)) return null;
    const net = g.netOwed?.[myPlayerKey];
    if (net == null || net === 0) return null;
    return formatMoney(net);
  };

  const pressLinesForGame = (g: GameResult): PressSettlementLine[] => {
    const raw = g.detail?.pressLines;
    if (!Array.isArray(raw)) return [];
    return raw as PressSettlementLine[];
  };

  const addMedia = (files: FileList | null) => {
    if (!files?.length) return;
    setMediaFiles((prev) => [...prev, ...Array.from(files)].slice(0, 6));
  };

  const postRecap = async () => {
    if (!round || !canPostRecap) return;
    setPostError(null);
    setPosting(true);
    try {
      const media_urls: string[] = [];
      try {
        for (let i = 0; i < mediaFiles.length; i += 1) {
          const f = mediaFiles[i];
          const fromName = f.name.split('.').pop()?.toLowerCase();
          const mimeExt = f.type.startsWith('video/')
            ? (f.type.includes('quicktime') ? 'mov' : f.type.includes('webm') ? 'webm' : 'mp4')
            : 'jpg';
          const ext = fromName || mimeExt;
          media_urls.push(await uploadFile('post-photos', f, `recap-${i + 1}.${ext}`));
        }
        await publishGroupRound({
          round,
          member,
          courseName,
          media_urls: media_urls.length ? media_urls : undefined,
          body: postBody.trim() || undefined,
          onCallSettled: (msg) => {
            window.dispatchEvent(new CustomEvent('bagtag:call-settled', { detail: msg }));
          },
        });
      } catch (err) {
        await Promise.all(media_urls.map((url) => deleteUploadedFile(url).catch(() => {})));
        throw err;
      }
      setPosted(true);
      window.dispatchEvent(new Event('bagtag:refresh-feed'));
      onPosted?.();
    } catch (err) {
      setPostError((err as Error).message || 'Could not post recap');
    } finally {
      setPosting(false);
    }
  };

  const confirmDelete = async () => {
    if (!round || !isOrganizer) return;
    setDeleting(true);
    try {
      await db.groupRounds.delete(roundId);
      removeRoundFromPlayTabCache(roundId);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      setDeleteError((err as Error).message || 'Could not delete round');
    } finally {
      setDeleting(false);
    }
  };

  if (!round) {
    return <Sheet title="Results" onClose={onClose}><div className="p-4 text-sm text-mute">Loading…</div></Sheet>;
  }

  const highlightId = round.players?.find((p) => p.member_id === member.id)?.member_id
    || round.players?.find((p) => p.member_id === member.id)?.player_key
    || member.id;

  return (
    <>
    <Sheet title="Round Results" onClose={onClose}>
      <div className="space-y-4 px-4 pb-8 pt-2">
        <div className="rounded-xl border border-gold bg-gold/10 p-3.5">
          <div className="font-serif text-base font-bold text-navy">{courseName}</div>
          <div className="text-xs text-ink-soft">
            {new Date(round.played_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
          </div>
          <div className="mt-1 text-xs text-mute">
            {(round.players?.length ?? 0)} player{(round.players?.length ?? 0) === 1 ? '' : 's'}
            {round.players?.length ? ` · ${round.players.map((p) => p.display_name.split(' ')[0]).join(', ')}` : ''}
          </div>
          {isCasual && (
            <div className="mt-2 text-xs font-semibold text-ink-soft">Just play — no side games</div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowScorecard((v) => !v)}
          className="w-full rounded-lg border border-line py-2 text-xs font-bold text-navy"
        >
          {showScorecard ? 'Hide' : 'Show'} scorecard
        </button>

        {showScorecard && (
          hasHoleScores && holes.length > 0 ? (
            <GroupScorecardGrid
              holes={holes}
              players={gridPlayers}
              range={scorecardRange}
              highlightPlayerId={highlightId}
            />
          ) : (
            <div className="rounded-xl border border-line bg-paper p-3">
              <div className="mb-2 text-[10px] font-extrabold tracking-wide text-mute">
                TOTALS ONLY
              </div>
              <p className="mb-2 text-[11px] text-mute">
                Hole-by-hole scores are not available for this round.
              </p>
              <div className="space-y-1.5">
                {totalsFallback.map((row) => (
                  <div
                    key={row.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                      row.you ? 'border-gold bg-gold/10' : 'border-line bg-cream'
                    }`}
                  >
                    <span className="font-semibold text-navy">{row.name}</span>
                    <span className="font-mono font-bold text-navy">{row.total ?? '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        <div>
          <div className="mb-2 text-[10px] font-extrabold tracking-wide text-navy">
            {isCasual ? 'LEADERBOARD' : 'ROUND HIGHLIGHTS'}
          </div>
          {gameResults.length === 0 ? (
            <div className="text-xs text-mute">No standings recorded.</div>
          ) : gameResults.map((g, i) => {
            const won = g.winners?.includes(myPlayerKey || '');
            const roundGame = round?.games?.find((rg) => rg.format_id === g.formatId);
            const stakeCfg = roundGame
              ? stakeFromDbRow(g.formatId, (roundGame.stakes || {}) as Record<string, unknown>)
              : null;
            const scoringLabel = stakeCfg ? scoringTypeLabel(migrateScoringType(stakeCfg.scoringType)) : null;
            return (
              <div key={g.formatId || i} className="mb-2 rounded-lg border border-line bg-paper px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-bold text-ink">{g.label || g.formatId}</div>
                  {scoringLabel && (
                    <span className="shrink-0 rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-navy">
                      {scoringLabel}
                    </span>
                  )}
                </div>
                {won && (
                  <div className="text-xs font-semibold text-good">You won {g.label || g.formatId}</div>
                )}
                <div className="text-xs text-ink-soft">{g.summary}</div>
                {g.standings && g.standings.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {g.standings.map((s, j) => (
                      <div key={j} className="flex justify-between text-[11px]">
                        <span>{s.name}</span>
                        <span className="font-semibold text-navy">{s.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hasMoneyStakes && gameResults.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowSettlement((v) => !v)}
              className="w-full rounded-lg border border-line py-2 text-xs font-bold text-navy"
            >
              {showSettlement ? 'Hide' : 'View'} settlement details
            </button>
            {showSettlement && (
              <div className="space-y-2 rounded-xl border border-gold/40 bg-gold/5 p-3">
                <div className="text-[10px] font-extrabold tracking-wide text-gold-deep">PRIVATE SETTLEMENT</div>
                <p className="text-[10px] text-mute">Not shown on the public feed recap — amounts only visible here.</p>

                <div className="rounded-lg border border-line bg-paper px-3 py-2.5">
                  <div className="mb-2 text-[10px] font-extrabold tracking-wide text-navy">PAYOUTS</div>
                  {payoutRows.length === 0 ? (
                    <div className="text-xs font-semibold text-good">All settled — no payments due</div>
                  ) : (
                    <div className="space-y-1.5">
                      {payoutRows.map((row) => (
                        <div
                          key={`${row.fromKey}-${row.toKey}-${row.amountCents}`}
                          className={`rounded-lg border px-2.5 py-2 text-xs ${
                            row.involvesViewer
                              ? 'border-gold bg-gold/10 font-semibold text-navy'
                              : 'border-line bg-cream text-ink'
                          }`}
                        >
                          {payoutSentence(row)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {gameResults.map((g, i) => {
                  const moneyLine = gameMoneyLine(g);
                  const pressLines = pressLinesForGame(g);
                  return (
                    <div key={`settle-${g.formatId || i}`} className="rounded-lg border border-line bg-paper px-3 py-2 text-xs">
                      <div className="font-bold text-navy">{g.label || g.formatId}</div>
                      {moneyLine && (
                        <div className={`font-extrabold ${moneyLine.startsWith('+') ? 'text-good' : 'text-bad'}`}>
                          Your result: {moneyLine}
                        </div>
                      )}
                      {!moneyLine && (
                        <div className="text-mute">Your result: even</div>
                      )}
                      {pressLines.length > 0 && (
                        <div className="mt-2 space-y-1 border-t border-line pt-2">
                          <div className="text-[9px] font-bold uppercase tracking-wide text-mute">Presses</div>
                          {pressLines.map((line) => {
                            const pressNet = myPlayerKey ? line.netOwed[myPlayerKey] : undefined;
                            if (pressNet == null || pressNet === 0) return null;
                            const lineMoney = formatMoney(pressNet);
                            return (
                              <div key={line.press.id} className="flex justify-between gap-2">
                                <span className="text-ink-soft">{line.label}</span>
                                <span className={`shrink-0 font-bold ${lineMoney.startsWith('+') ? 'text-good' : 'text-bad'}`}>
                                  {lineMoney}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {clubPoints && clubPoints.items.length > 0 && (
          <div className="rounded-xl border border-gold/50 bg-gold/5 p-3.5">
            <div className="mb-2 text-sm font-bold text-navy">🏆 Bag Tag Club Points Earned</div>
            <div className="space-y-1.5">
              {clubPoints.items.map((item, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-ink-soft">{item.label}</span>
                  <span className="font-bold text-navy">+{item.points}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between border-t border-gold/30 pt-2 text-sm font-extrabold text-navy">
              <span>Total Club Points Earned</span>
              <span>{clubPoints.total}</span>
            </div>
            {!posted && !round.legacy_post_id && (
              <p className="mt-2 text-[10px] text-mute">
                Club points are applied when you post to the feed.
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowLog((v) => !v)}
          className="w-full rounded-lg border border-line py-2 text-xs font-bold text-navy"
        >
          {showLog ? 'Hide' : 'Show'} score edit history ({log.length})
        </button>

        {showLog && (
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-line bg-paper p-2">
            {log.length === 0 ? (
              <div className="text-xs text-mute">No edits logged.</div>
            ) : log.map((e) => (
              <div key={e.id} className="text-[10px] text-ink-soft">
                Hole {e.hole_num} · {playerName(e.player_key)}: {e.old_strokes ?? '—'} → {e.new_strokes ?? '—'} ·{' '}
                {new Date(e.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </div>
            ))}
          </div>
        )}

        {canPostRecap && !posted && !round.legacy_post_id && (
          <div className="space-y-3 rounded-xl border-2 border-navy bg-paper p-3.5">
            <div>
              <div className="text-sm font-bold text-navy">Post Recap</div>
              <p className="mt-1 text-[11px] leading-relaxed text-mute">
                Review scores and highlights, add photos or video, then confirm. Any player in the group can post once.
              </p>
            </div>
            <textarea
              value={postBody}
              onChange={(e) => setPostBody(e.target.value)}
              placeholder={`Great round at ${courseName}…`}
              rows={2}
              className="w-full rounded-lg border border-line bg-cream px-3 py-2 text-sm"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={(e) => { addMedia(e.target.files); e.target.value = ''; }}
            />
            {mediaFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {mediaFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative">
                    {f.type.startsWith('video/') ? (
                      <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-navy text-[10px] font-bold text-cream">
                        VIDEO
                      </div>
                    ) : (
                      <img src={URL.createObjectURL(f)} alt="" className="h-16 w-16 rounded-lg object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => setMediaFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-navy text-cream"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 text-xs font-bold text-navy"
            >
              <Camera size={14} /> Add photos or video
            </button>
            {postError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {postError}
              </div>
            )}
            <button
              type="button"
              disabled={posting}
              onClick={() => void postRecap()}
              className="w-full rounded-xl bg-navy py-3.5 text-sm font-bold text-cream disabled:opacity-60"
            >
              {posting ? 'Posting…' : 'Confirm & Post Recap'}
            </button>
          </div>
        )}
        {!canPostRecap && !posted && !round.legacy_post_id && (
          <div className="text-center text-xs text-mute">Only players in this round can post the recap.</div>
        )}
        {(posted || round.legacy_post_id) && (
          <div className="text-center text-xs font-semibold text-good">Recap posted to feed</div>
        )}

        {isOrganizer && (
          <button
            type="button"
            disabled={deleting}
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full rounded-lg border border-bad/30 bg-bad/5 py-2.5 text-xs font-bold text-bad disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Delete round'}
          </button>
        )}
      </div>
    </Sheet>
    <ConfirmDialog
      open={showDeleteConfirm}
      title="Delete this round?"
      message="It will be removed from your history. This cannot be undone."
      confirmLabel="Delete"
      destructive
      busy={deleting}
      onConfirm={() => void confirmDelete()}
      onCancel={() => setShowDeleteConfirm(false)}
    />
    <AlertDialog
      open={deleteError != null}
      title="Could not delete round"
      message={deleteError}
      onClose={() => setDeleteError(null)}
    />
    </>
  );
}
